import { ANNOTATION_COLORS, ANNOTATION_CHROME } from "../tokens";
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { postMutation } from "../api";
import { authHeaders } from "../auth/token";
import { buildPhotoAnnotationBatch, COLOR_OPTIONS, renderAnnotationToCanvas, type Annotation, type AnnotationColor, type AnnotationTool } from "./photoAnnotationDrawing";

const button = { padding: "9px 14px", borderRadius: 9, border: `1px solid ${ANNOTATION_CHROME.border}`, background: ANNOTATION_CHROME.button, color: "white", cursor: "pointer", font: "inherit" };

export function PhotoAnnotationEditor({ imageUrl, visitId, photoId, search, onCancel, onSaved }: {
  imageUrl: string; visitId: string; photoId: string; search: string; onCancel: () => void; onSaved: () => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const source = useRef<HTMLImageElement | null>(null);
  const draft = useRef<Annotation | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [tool, setTool] = useState<AnnotationTool>("draw");
  const [color, setColor] = useState<AnnotationColor>(ANNOTATION_COLORS.red);
  const [text, setText] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [size, setSize] = useState({ width: 1, height: 1 });
  const repaint = () => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx || !source.current) return;
    ctx.clearRect(0, 0, el.width, el.height);
    ctx.drawImage(source.current, 0, 0, el.width, el.height);
    for (const annotation of annotations) renderAnnotationToCanvas(ctx, annotation, el.width, el.height);
    if (draft.current) renderAnnotationToCanvas(ctx, draft.current, el.width, el.height);
  };
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;
    const controller = new AbortController();
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      if (cancelled) return;
      source.current = image;
      setSize({ width: image.naturalWidth, height: image.naturalHeight });
      setReady(true);
    };
    image.onerror = () => { if (!cancelled) setError("Could not load this photo for annotation. Close and try again."); };
    void (async () => {
      try {
        const response = await fetch(imageUrl, { headers: await authHeaders(), signal: controller.signal });
        if (!response.ok) throw new Error("Could not load this photo for annotation.");
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        image.src = objectUrl;
      } catch { if (!cancelled) setError("Could not load this photo for annotation. Close and try again."); }
    })();
    return () => { cancelled = true; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [imageUrl]);
  useEffect(repaint, [annotations, ready, size]);
  const point = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) };
  };
  const save = async () => {
    const el = canvas.current;
    if (!el || !ready || busy) return;
    setBusy(true); setError("");
    try {
      const thumb = document.createElement("canvas");
      const scale = Math.min(1, 900 / el.width);
      thumb.width = Math.max(1, Math.round(el.width * scale));
      thumb.height = Math.max(1, Math.round(el.height * scale));
      const ctx = thumb.getContext("2d");
      if (!ctx) throw new Error("Could not prepare the photo thumbnail.");
      ctx.drawImage(el, 0, 0, thumb.width, thumb.height);
      const result = await postMutation(`/api/walks/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}/annotation`, search, {
        base64Data: el.toDataURL("image/jpeg", 0.94).split(",")[1], mimeType: "image/jpeg",
        thumbnailBase64Data: thumb.toDataURL("image/jpeg", 0.86).split(",")[1], thumbnailMimeType: "image/jpeg",
        annotationBatch: buildPhotoAnnotationBatch(annotations),
      });
      if (!result.ok) throw new Error(result.error || "Could not save annotations. Try again.");
      onSaved();
    } catch (error) { setError(error instanceof Error ? error.message : "Could not save annotations."); }
    finally { setBusy(false); }
  };
  return <div data-testid="photo-annotation-editor" style={{ position: "absolute", inset: 0, zIndex: 3, background: ANNOTATION_CHROME.background, display: "flex", flexDirection: "column", padding: 20, gap: 14 }}>
    <div role="toolbar" aria-label="Photo annotation tools" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      {([ ["draw", "Draw"], ["arrow", "Arrow"], ["rect", "Box"], ["text", "Text"]] as const).map(([value, label]) => <button key={value} style={{ ...button, background: tool === value ? ANNOTATION_CHROME.onBackground : ANNOTATION_CHROME.button, color: tool === value ? ANNOTATION_CHROME.background : ANNOTATION_CHROME.onBackground }} aria-pressed={tool === value} disabled={busy} onClick={() => setTool(value)}>{label}</button>)}
      <button style={button} disabled={busy || !annotations.length} onClick={() => setAnnotations((items) => items.slice(0, -1))}>Undo</button>
      <span style={{ flex: 1 }} />
      <button style={button} disabled={busy} onClick={onCancel}>Cancel</button>
      <button style={{ ...button, background: ANNOTATION_CHROME.save, color: ANNOTATION_CHROME.background }} disabled={busy || !ready || !annotations.length} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      {COLOR_OPTIONS.map((value, i) => <button key={value} aria-label={["White", "Black", "Red", "Green", "Blue", "Yellow"][i]} aria-pressed={color === value} disabled={busy} onClick={() => setColor(value)} style={{ width: 28, height: 28, borderRadius: "50%", background: value, border: `2px solid ${ANNOTATION_CHROME.swatchBorder}`, outline: color === value ? "2px solid white" : undefined, outlineOffset: 2 }} />)}
      {tool === "text" && <input aria-label="Annotation text" placeholder="Type your callout, then click the photo" value={text} onChange={(e) => setText(e.target.value)} maxLength={200} disabled={busy} style={{ ...button, width: 300 }} />}
      <span style={{ color: ANNOTATION_CHROME.hint, fontSize: 13.8 }}>{tool === "text" ? "Click the photo to place text." : "Drag on the photo to annotate."}</span>
    </div>
    {error && <div role="alert" style={{ color: ANNOTATION_CHROME.error }}>{error}</div>}
    <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
      {!ready && !error && <span style={{ color: ANNOTATION_CHROME.hint }}>Loading photo…</span>}
      <canvas ref={canvas} width={size.width} height={size.height} aria-label="Photo annotation canvas" style={{ display: ready ? "block" : "none", maxWidth: "100%", maxHeight: "100%", objectFit: "contain", touchAction: "none", cursor: "crosshair" }}
        onPointerDown={(event) => {
          if (!ready || busy || event.button !== 0 || annotations.length >= 50) return;
          const p = point(event);
          if (tool === "text") {
            if (text.trim()) setAnnotations((items) => [...items, { id: crypto.randomUUID(), type: "text", color, size: 0.05, point: p, text: text.trim() }]);
            return;
          }
          event.currentTarget.setPointerCapture(event.pointerId);
          draft.current = tool === "draw" ? { id: crypto.randomUUID(), type: "path", color, width: 0.0075, points: [p] } : { id: crypto.randomUUID(), type: tool, color, width: 0.0075, from: p, to: p };
        }}
        onPointerMove={(event) => {
          if (!draft.current || busy) return;
          const p = point(event);
          if (draft.current.type === "path") draft.current.points.push(p);
          else if (draft.current.type !== "text") draft.current.to = p;
          repaint();
        }}
        onPointerUp={(event) => {
          if (!draft.current) return;
          const completed = draft.current;
          draft.current = null;
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
          if (completed.type !== "path" || completed.points.length > 1) setAnnotations((items) => [...items, completed]);
        }}
        onPointerCancel={() => { draft.current = null; repaint(); }}
      />
    </div>
  </div>;
}
